import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { baseFormSchema, type BaseFormData } from '@/schema/template';
import {
  useCreateDepartmentMutation,
  useCreateTouchpointMutation,
  useCreateSubTouchpointMutation,
  useCreateTitleMutation,
} from '@/hooks/queries/useTemplates';

interface BaseFormProps {
  columnType: 'department' | 'touchpoint' | 'subtouchpoint' | 'title';
  propertyTypeId: string | number;
  onAdd: (data: Record<string, string>) => void;
  onSuccess?: () => void;
}

export function BaseForm({
  columnType,
  propertyTypeId,
  onAdd,
  onSuccess,
}: BaseFormProps) {
  const createDepartmentMutation = useCreateDepartmentMutation({
    onSuccessCallback: () => {
      onSuccess?.();
    },
  });
  const createTouchpointMutation = useCreateTouchpointMutation({
    onSuccessCallback: () => {
      onSuccess?.();
    },
  });
  const createSubTouchpointMutation = useCreateSubTouchpointMutation({
    onSuccessCallback: () => {
      onSuccess?.();
    },
  });
  const createTitleMutation = useCreateTitleMutation({
    onSuccessCallback: () => {
      onSuccess?.();
    },
  });

  const form = useForm<BaseFormData>({
    resolver: zodResolver(baseFormSchema),
    defaultValues: {
      nameEnglish: '',
      nameArabic: '',
    },
    mode: 'onChange',
  });

  const onSubmit = (data: BaseFormData) => {
    const requestData = {
      property_type_id: Number(propertyTypeId),
      name_en: data.nameEnglish,
      name_ar: data.nameArabic,
    };

    switch (columnType) {
      case 'department':
        createDepartmentMutation.mutate(requestData);
        break;
      case 'touchpoint':
        createTouchpointMutation.mutate(requestData);
        break;
      case 'subtouchpoint':
        createSubTouchpointMutation.mutate(requestData);
        break;
      case 'title':
        createTitleMutation.mutate(requestData);
        break;
    }

    onAdd(data as Record<string, string>);
    form.reset();
  };

  const isLoading =
    createDepartmentMutation.isPending ||
    createTouchpointMutation.isPending ||
    createSubTouchpointMutation.isPending ||
    createTitleMutation.isPending;

  return (
    <form
      onSubmit={form.handleSubmit(onSubmit)}
      className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg border"
    >
      <div className="flex-1 grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label htmlFor="nameEnglish">English Name</Label>
          <Controller
            name="nameEnglish"
            control={form.control}
            render={({ field }) => (
              <Input
                {...field}
                id="nameEnglish"
                placeholder={`Enter ${columnType} name in English`}
                className={
                  form.formState.errors.nameEnglish ? 'border-red-500' : ''
                }
              />
            )}
          />
          {form.formState.errors.nameEnglish && (
            <p className="text-sm text-red-500">
              {form.formState.errors.nameEnglish.message}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="nameArabic">Arabic Name</Label>
          <Controller
            name="nameArabic"
            control={form.control}
            render={({ field }) => (
              <Input
                {...field}
                id="nameArabic"
                placeholder={`أدخل اسم ${columnType} بالعربية`}
                className={`text-right ${form.formState.errors.nameArabic ? 'border-red-500' : ''}`}
                dir="rtl"
              />
            )}
          />
          {form.formState.errors.nameArabic && (
            <p className="text-sm text-red-500">
              {form.formState.errors.nameArabic.message}
            </p>
          )}
        </div>
      </div>

      <Button
        type="submit"
        size="sm"
        disabled={!form.formState.isValid || isLoading}
        className="h-8 w-8 p-0"
      >
        <Plus className="w-4 h-4" />
      </Button>
    </form>
  );
}
